import {
  AvatarCustomer,
  AvatarProps,
  FormAutocompleteField,
  FormAutocompleteFieldProps,
  CustomerListItem,
} from '@razz-team/ui-web';
import { useCallback, useMemo } from 'react';
import { useBusinessMembersQuery } from '../../api/members';
import { useBusinessQuery } from '../../api/businesses';

export type AccountHolderFieldProps<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
> = Omit<
  FormAutocompleteFieldProps<string, Multiple, DisableClearable>,
  'options' | 'getOptionLabel'
>;

export function AccountHolderField<
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>(props: AccountHolderFieldProps<Multiple, DisableClearable>) {
  const membersQuery = useBusinessMembersQuery();
  const businessQuery = useBusinessQuery();

  const members = membersQuery.data?.members;
  const business = businessQuery.data?.profile;

  const customers = useMemo(() => {
    if (!members || !business) return [];
    return [
      business,
      ...members.map(({ customer, identity }) => ({
        id: customer.id,
        name: customer.name ?? identity.email ?? identity.unconfirmedEmail ?? customer.username,
        username: customer.username,
      })),
    ];
  }, [members, business]);
  const options = useMemo(() => customers.map(({ id }) => id), [customers]);

  const getOptionLabel = useCallback(
    (option: string) => {
      const customer = customers.find(({ id }) => id === option);
      return customer?.name ?? customer?.username ?? option;
    },
    [customers],
  );

  const renderOption = useCallback(
    (optionProps: React.HTMLAttributes<HTMLLIElement>, option: string) => {
      const customer = customers.find(({ id }) => id === option);
      if (!customer) return null;

      return <CustomerListItem customer={customer} {...optionProps} key={option} />;
    },
    [customers],
  );

  const renderAvatar = useCallback(
    (option: string, avatarProps: Partial<AvatarProps>) => {
      const customer = customers.find(({ id }) => id === option);
      if (!customer) return null;

      return <AvatarCustomer customer={customer} {...avatarProps} />;
    },
    [customers],
  );

  return (
    <FormAutocompleteField<string, Multiple, DisableClearable>
      options={options}
      loading={membersQuery.isPending || businessQuery.isPending}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderAvatar={renderAvatar}
      {...props}
    />
  );
}
